import React, { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  MenuItem,
} from '@material-ui/core';
import { ModalContainer, StyledPaper } from './UserRequestsStyles';
import { RequestStatus } from './types';
import { SUPPORT_REQUEST_STATUS_TRANSITIONS } from './SupportRequests/status';
import { FlexBox } from '../global/FlexBox';

export type EditStatusModalProps = {
  open: boolean;
  currentStatus: RequestStatus;
  onClose: () => void;
  onSave: (nextStatus: RequestStatus, reason?: string) => void;
  isLoading?: boolean;
};

function getAllowedTransitions(status: RequestStatus): RequestStatus[] {
  return SUPPORT_REQUEST_STATUS_TRANSITIONS[status] ?? [];
}

function isReasonRequired(next: RequestStatus): boolean {
  return ['Resolved', 'Canceled', 'Reopened'].includes(next);
}

const EditStatusModal: React.FC<EditStatusModalProps> = ({
  open,
  currentStatus,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const options = useMemo(
    () => getAllowedTransitions(currentStatus),
    [currentStatus],
  );

  const schema = useMemo(
    () =>
      yup.object({
        status: yup
          .mixed<RequestStatus>()
          .oneOf(options)
          .required('Status is required'),
        reason: yup
          .string()
          .when('status', ([status], reasonField) =>
            isReasonRequired(status)
              ? reasonField
                  .required('Reason is required')
                  .min(2, 'Reason is too short')
              : reasonField.optional(),
          ),
      }),
    [options],
  );

  const { control, handleSubmit, reset, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { status: undefined, reason: '' },
  });

  const submit = (values: { status: RequestStatus; reason?: string }) => {
    onSave(values.status as RequestStatus, values.reason?.trim());
    reset({ status: undefined, reason: '' });
  };

  return (
    <ModalContainer open={open} onClose={onClose}>
      <StyledPaper>
        {isLoading ? (
          <FlexBox
            sx={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </FlexBox>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="h6">Edit ticket's status</Typography>
            </Box>
            <form onSubmit={handleSubmit(submit)} noValidate>
              <Box display="flex" flexDirection="column" gridGap={8}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      select
                      {...field}
                      variant="outlined"
                      fullWidth
                      required
                      label="Choose the status"
                      error={!!error}
                      helperText={error?.message || ' '}
                    >
                      {options.map(status => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  name="reason"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Reason for status change"
                      variant="outlined"
                      fullWidth
                      required={isReasonRequired(
                        watch('status') as RequestStatus,
                      )}
                      error={!!error}
                      helperText={error?.message || ' '}
                    />
                  )}
                />
              </Box>
              <Box
                mt={3}
                display="flex"
                justifyContent="flex-end"
                gridColumnGap={24}
              >
                <Button onClick={onClose} variant="text" color="primary">
                  Dismiss
                </Button>
                <Button type="submit" variant="text" color="primary">
                  Save
                </Button>
              </Box>
            </form>
          </>
        )}
      </StyledPaper>
    </ModalContainer>
  );
};

export default EditStatusModal;
